import { fetchAllPages } from './utils/pagination'
import * as queries from './graphql/queries'
import type { EnvironmentKey } from './types/environment'

interface GraphQLQuery {
  query: string
  variables?: Record<string, any>
}

interface GraphQLClient {
  graphql: (params: GraphQLQuery) => Promise<any>
}

interface QueryConfig {
  query: string
  dataKey: string
}

interface QueryMapType {
  [key: string]: QueryConfig
}

export const getClient = (): GraphQLClient => {
  // Return a wrapper that uses the unified GraphQL client
  return {
    graphql: async ({ query, variables = {} }: GraphQLQuery): Promise<any> => {
      // Extract query name for logging
      const queryString = query.toString()
      const queryMatch = queryString.match(/query\s+(\w+)/)
      const queryName = queryMatch ? queryMatch[1] : 'unknown'

      const clientModule = await import('./utils/unifiedGraphQLClient.js')
      const executeGraphQL = clientModule.executeGraphQL
      return await executeGraphQL(query, variables)
    }
  }
}

// Helper function using unified GraphQL client
export const callExternalApi = async (
  environment: string | EnvironmentKey | null | undefined,
  query: string,
  variables: Record<string, any> = {}
): Promise<any> => {
  try {
    const clientModule = await import('./utils/unifiedGraphQLClient.js')
    const executeGraphQL = clientModule.executeGraphQL

    // Map query names to their corresponding GraphQL queries and data keys
    const queryMap: QueryMapType = {
      'listSERVICE_PROVIDERS': { query: queries.listServiceProvider, dataKey: 'listSERVICE_PROVIDER' },
      'ListServiceProviders': { query: queries.listServiceProvider, dataKey: 'listSERVICE_PROVIDER' },
      'listSERVICES': { query: queries.listService, dataKey: 'listSERVICE' },
      'ListServices': { query: queries.listService, dataKey: 'listSERVICE' },
      'listSERVICE_PARAMS': { query: queries.listServiceParam, dataKey: 'listSERVICE_PARAM' },
      'ListServiceParams': { query: queries.listServiceParam, dataKey: 'listSERVICE_PARAM' },
      'listSERVICE_PARAM_MAPPINGS': { query: queries.listServiceParamMapping, dataKey: 'listSERVICE_PARAM_MAPPING' },
      'listServiceParamMappings': { query: queries.listServiceParamMapping, dataKey: 'listSERVICE_PARAM_MAPPING' },
      'listREDIRECT_URLS': { query: queries.listRedirectUrl, dataKey: 'listREDIRECT_URL' },
      'listRedirectUrls': { query: queries.listRedirectUrl, dataKey: 'listREDIRECT_URL' },
      'listSTEP_SERVICE_MAPPINGS': { query: queries.listStepServiceMapping, dataKey: 'listSTEP_SERVICE_MAPPING' },
      'listSTEP_TYPES': { query: queries.listStepType, dataKey: 'listSTEP_TYPE' },
      'listORIGIN_PRODUCTS': { query: queries.listOriginProduct, dataKey: 'listORIGIN_PRODUCT' },
      'listSERVICE_EXPR_MAPPINGS': { query: queries.listServiceExprMapping, dataKey: 'listSERVICE_EXPR_MAPPING' },
      'listSERVICE_DOMAINS': { query: queries.listServiceDomain, dataKey: 'listSERVICE_DOMAIN' },
      'listSERVICE_RESPONSES': { query: queries.listServiceResponse, dataKey: 'listSERVICE_RESPONSE' },
      'listSERVICE_RESPONSE_MAPPINGS': { query: queries.listServiceResponseMapping, dataKey: 'listSERVICE_RESPONSE_MAPPING' },
      'listSORT_CRITERIAS': { query: queries.listSortCriteria, dataKey: 'listSORT_CRITERIA' },
      'listSTEP_TYPE_PARAM_MAPS': { query: queries.listStepTypeParamMap, dataKey: 'listSTEP_TYPE_PARAM_MAP' },
      'listFILTER_CRITERIAS': { query: queries.listFilterCriteria, dataKey: 'listFILTER_CRITERIA' },
      'listDATA_ELEMENTS': { query: queries.listDataElement, dataKey: 'listDATA_ELEMENT' },
      'listCONFIG_PARAMS': { query: queries.listConfigParam, dataKey: 'listCONFIG_PARAM' }
    }

    // Find matching query configuration
    let queryConfig: QueryConfig | null = null
    const entries = Object.entries(queryMap)
    for (let i = 0; i < entries.length; i++) {
      const key = entries[i][0]
      const config = entries[i][1]
      if (query.includes(key)) {
        queryConfig = config
        break
      }
    }

    if (queryConfig) {
      // Use unified client with pagination
      const client: GraphQLClient = {
        graphql: async (params: GraphQLQuery) => executeGraphQL(params.query, params.variables, environment as EnvironmentKey | null)
      }
      let items = await fetchAllPages(client, queryConfig.query, variables, queryConfig.dataKey)

      // Filter SERVICE_PARAM by service if a service filter is set
      if (queryConfig.dataKey === 'listSERVICE_PARAM') {
        if (variables.serviceId) {
          items = items.filter((param: any) => param.SERVICE_ID === parseInt(variables.serviceId))
        }
      }

      const transformedData = {
        data: {
          [queryConfig.dataKey]: {
            items
          }
        }
      }

      return transformedData
    } else {
      // Handle mutation operations or other non-list queries
      const result = await executeGraphQL(query, variables, environment as EnvironmentKey | null)
      return result
    }
  } catch (error) {
    throw error
  }
}
